(() => {
  'use strict';
  const recordIds = new Set([9189000216832, 9189044912384, 9189049204992]);
  const rushVariants = Object.freeze({
    media: 49775466152192,
    priority: 49775466184960,
    express: 49775466217728
  });
  const rushPrices = Object.freeze({media: 4000, priority: 6000, express: 12000});
  const rushIds = new Set(Object.values(rushVariants));
  const root = window.Shopify?.routes?.root || '/';
  const config = JSON.parse(document.getElementById('lcvr-order-policy-labels')?.textContent || '{}');
  const text = (key, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replaceAll('__' + k.toUpperCase() + '__', String(v)), config[key] || key);
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: window.Shopify?.currency?.active || 'USD'}).format(cents / 100);
  const units = n => Number.isInteger(n) && n >= 1 && n <= 100 ? Math.ceil(n / 10) : 0;
  const count = cart => cart.items.filter(i => recordIds.has(Number(i.product_id))).reduce((n, i) => n + i.quantity, 0);
  const rushLines = cart => cart.items.filter(i => rushIds.has(Number(i.variant_id)));
  const normalizeRush = value => {
    const v = String(value || '').toLowerCase();
    if (v.includes('express')) return 'express';
    if (v.includes('priority')) return 'priority';
    return 'media';
  };
  const rushVariantFor = value => rushVariants[normalizeRush(value)];
  const rushPriceFor = value => value ? rushPrices[normalizeRush(value)] : 0;
  const shippingPreferenceFor = value => ({
    media: 'Media Mail',
    priority: 'Priority Mail',
    express: 'Priority Mail Express'
  })[normalizeRush(value)];

  async function request(path, body) {
    const response = await fetch(root + path, {credentials: 'same-origin', cache: 'no-store', ...(body ? {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)} : {})});
    const result = await response.json();
    if (!response.ok) throw Error(result.description || text('cart_error'));
    return result;
  }

  function validate(cart) {
    const n = count(cart), lines = rushLines(cart);
    if (n > 100) return text('limit');
    if (lines.length && !n) return text('orphan_rush');
    if (lines.length > 1) return text('multiple_rush');
    return '';
  }

  async function syncRush(cart) {
    const error = validate(cart);
    if (error) throw Error(error);
    const line = rushLines(cart)[0], quantity = units(count(cart));
    if (!line || line.quantity === quantity) return {cart, changed: false};
    const result = await request('cart/update.js', {updates: {[line.key]: quantity}});
    if (rushLines(result).length !== 1 || rushLines(result)[0].quantity !== units(count(result))) throw Error(text('cart_error'));
    return {cart: result, changed: true};
  }

  let adding = false;
  async function addRush(choice = 'media') {
    if (adding) throw Error(text('wait'));
    adding = true;
    try {
      const cart = await request('cart.js'), n = count(cart), error = validate(cart);
      if (error) throw Error(error);
      if (!units(n)) throw Error(text('records_first'));
      const existing = rushLines(cart)[0];
      if (existing) return (await syncRush(cart)).cart;
      const type = normalizeRush(choice);
      return await request('cart/add.js', {items: [{
        id: rushVariants[type],
        quantity: units(n),
        properties: {
          'Service': text('production_only'),
          'Shipping preference': shippingPreferenceFor(type),
          'Rush pricing': text('per_ten')
        }
      }]});
    } finally { adding = false; }
  }

  function estimate(n, options = {}) {
    if (!units(n) || n > 100) return null;
    const minutesPerSide = Math.max(0, Number(options.minutesPerSide) || 0);
    const sides = Math.max(1, Number(options.sides) || 2);
    const printedArtwork = !!options.printedArtwork;
    const cncSelected = !!options.cncSelected;
    const size = Number(options.size) || 0;
    const rush = !!options.rush;
    const quickSingleSeven = !!options.quickSingleSeven;
    const blankSheets = Math.ceil(n / 10);

    // Universal quantity workload. The minimum and maximum both move later
    // as copy count rises so doubling a run cannot produce nearly the same date.
    const base = quickSingleSeven ? [1, 2]
      : n <= 10 ? [3, 5]
      : n <= 20 ? [5, 7]
      : n <= 30 ? [10, 14]
      : n <= 50 ? [11, 15]
      : n <= 75 ? [12, 17]
      : [14, 20];

    // Real-time cutting workload: the first two cutting hours fit inside normal
    // setup/QC; each additional three hours adds another production day.
    const totalCutMinutes = n * minutesPerSide * sides;
    const workloadDays = Math.ceil(Math.max(0, totalCutMinutes - 120) / 180);

    // Blanks are prepared in sheets of up to 10. Keep the one-week base buffer,
    // then add capacity as sheet count grows.
    const blankPrepDays = 5 + Math.floor(Math.max(0, blankSheets - 1) / 4);

    // Artwork and CNC run in parallel with record production rather than being
    // stacked on top of it. These are target completion windows from approval.
    const artworkTrackDays = printedArtwork
      ? (size === 7 ? [4, 5] : size === 12 ? [8, 10] : [5, 7])
      : null;
    const cncTrackDays = cncSelected ? [6, 8] : null;
    const parallelTrackDays = artworkTrackDays || cncTrackDays;

    // Rush changes queue position only. It does not erase physical prep,
    // cutting, artwork/CNC, or QC time.
    const rushQueueReduction = rush ? 5 : 0;
    const queueDays = base.map(d => Math.max(0, d - rushQueueReduction));
    const recordReadyDays = queueDays.map(d => d + blankPrepDays + workloadDays);
    const readyDays = parallelTrackDays
      ? [
          Math.max(recordReadyDays[0], parallelTrackDays[0]),
          Math.max(recordReadyDays[1], parallelTrackDays[1])
        ]
      : recordReadyDays.slice();

    const parallelExtraDays = parallelTrackDays
      ? [
          Math.max(0, readyDays[0] - recordReadyDays[0]),
          Math.max(0, readyDays[1] - recordReadyDays[1])
        ]
      : [0, 0];

    return {
      baseQueueDays: base,
      queueDays,
      rushQueueReduction,
      blankSheets,
      blankPrepDays,
      workloadDays,
      artworkTrackDays,
      cncTrackDays,
      parallelTrackDays,
      parallelExtraDays,
      totalCutMinutes,
      recordReadyDays,
      readyDays
    };
  }

  function leadDays(n, options = {}) {
    return estimate(n, options)?.readyDays || null;
  }

  function businessDate(start, days) {
    const date = new Date(start.getTime());
    while (days > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) days--;
    }
    return date;
  }

  function calendarDate(start, days) {
    const date = new Date(start.getTime());
    date.setDate(date.getDate() + Math.max(0, days));
    return date;
  }

  window.LCVROrderPolicy = Object.freeze({
    recordIds, rushIds, rushVariants, rushPrices,
    units, count, rushLines, validate, syncRush, addRush, request,
    text, money, estimate, leadDays, businessDate, calendarDate, rushVariantFor, rushPriceFor
  });

  window.LCVR_setRush = type => addRush(type);

  const init = () => {
    for (const section of document.querySelectorAll('[data-lcvr-rush-service]')) {
      if (section.dataset.ready) continue;
      section.dataset.ready = 'true';
      const button = section.querySelector('[data-rush-add]');
      const status = section.querySelector('[data-rush-status]');
      const select = section.querySelector('[data-shipping-preference]');
      const summary = section.querySelector('[data-rush-summary]');
      const render = cart => {
        const n = count(cart);
        const type = normalizeRush(select?.value);
        summary.textContent = n ? text('summary', {
          count: n,
          units: units(n),
          total: money(units(n) * rushPrices[type])
        }) : text('records_first');
      };
      request('cart.js').then(cart => {
        render(cart);
        select?.addEventListener('change', () => render(cart));
      }).catch(error => { status.textContent = error.message; });
      button?.addEventListener('click', async () => {
        button.disabled = true;
        try {
          await addRush(select?.value || 'media');
          location.assign(root + 'cart');
        } catch (error) {
          status.textContent = error.message;
          button.disabled = false;
        }
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();